import { Box, Group, SimpleGrid, Stack, Text, ThemeIcon, useMantineTheme } from '@mantine/core';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { IconClipboardList } from '@tabler/icons-react';
import { latest, previous } from '../types';
import type { PoamPoint } from '../types';
import { SafeTCardShell } from './SafeTCardShell';
import { AppTooltip } from '../../../common/AppTooltip';

export function PoamPipelineCard({ series }: { series: PoamPoint[] }) {
  const theme = useMantineTheme();
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const pipelineColors = ['#c5d7f2', '#9db9e8', '#6f98dd'] as const;
  const buckets: Array<{
    key: string;
    label: string;
    color: string;
    value: number;
    tooltip?: string;
  }> = [
    {
      key: 'unmitigatedNoPoam',
      label: 'No POAM',
      color: pipelineColors[0],
      value: cur?.unmitigatedNoPoam ?? 0,
      tooltip: 'Number of nodes this quarter with no vulnerabilities or weaknesses identified and no remediation action required.',
    },
    {
      key: 'poamOpenNotDeveloped',
      label: 'Open, Not Developed',
      color: pipelineColors[1],
      value: cur?.poamOpenNotDeveloped ?? 0,
      tooltip: 'Number of nodes this quarter with a vulnerability identified but no remediation plan created yet.',
    },
    {
      key: 'poamDevelopedNotMonitored',
      label: 'Developed, Not Monitored',
      color: pipelineColors[2],
      value: cur?.poamDevelopedNotMonitored ?? 0,
      tooltip: 'Number of nodes this quarter with an action plan but no progress tracking.',
    },
  ];

  const status = { label: 'Snapshot', color: 'gray' as const };
  const qoqLabel = (n: number | null): string =>
    n === null ? '— QoQ' : n === 0 ? '0.00% QoQ' : `${n > 0 ? '↑' : '↓'}${Math.abs(n).toFixed(2)}% QoQ`;

  const roundedTopShape = (props: any, isTop: boolean) => {
    const { x, y, width, height, fill } = props;
    if (!height || height <= 0) {
      return <rect x={x} y={y} width={width} height={0} fill="transparent" />;
    }
    if (!isTop) {
      return <rect x={x} y={y} width={width} height={height} fill={fill} />;
    }
    const r = Math.min(4, width / 2, height);
    const path = `M ${x},${y + r} Q ${x},${y} ${x + r},${y} L ${x + width - r},${y} Q ${x + width},${y} ${x + width},${y + r} L ${x + width},${y + height} L ${x},${y + height} Z`;
    return <path d={path} fill={fill} />;
  };

  const chartData = series.map((p) => ({
    quarter: p.quarter,
    unmitigatedNoPoam: p.unmitigatedNoPoam,
    poamOpenNotDeveloped: p.poamOpenNotDeveloped,
    poamDevelopedNotMonitored: p.poamDevelopedNotMonitored,
  }));

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  return (
    <SafeTCardShell
      title="Plan of Action and Milestones (POAM)"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      rightSlot={
        <ThemeIcon variant="light" size="lg">
          <IconClipboardList size={18} />
        </ThemeIcon>
      }
    >
      <Group position="apart" align="flex-start" spacing="lg" noWrap>
        <SimpleGrid
          cols={3}
          spacing="md"
          breakpoints={[
            { maxWidth: 900, cols: 2 },
            { maxWidth: 520, cols: 1 },
          ]}
        >
          {buckets.map((bucket) => {
            const curValue = bucket.value;
            const prevValue = prev ? (prev as any)[bucket.key] : null;
            const qoq =
              prevValue === null || prevValue === undefined
                ? null
                : prevValue === 0
                  ? curValue === 0
                    ? 0
                    : null
                  : ((curValue - prevValue) / prevValue) * 100;
            return (
              <Stack key={bucket.key} spacing={2}>
                {bucket.tooltip ? (
                  <AppTooltip label={bucket.tooltip} inline>
                    <Text
                      size="xs"
                      color="dimmed"
                      component="span"
                      sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
                    >
                      {bucket.label}
                    </Text>
                  </AppTooltip>
                ) : (
                  <Text size="xs" color="dimmed">
                    {bucket.label}
                  </Text>
                )}
                <Text size="lg" weight={800}>
                  {curValue}
                </Text>
                <Text size="xs" color="dimmed">
                  {qoqLabel(qoq)}
                </Text>
              </Stack>
            );
          })}
        </SimpleGrid>
      </Group>
      <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
        Quarterly POAM Status Distribution
      </Text>

      <Box sx={{ height: 190 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}
              opacity={0.25}
              vertical={false}
            />
            <XAxis
              dataKey="quarter"
              tick={{ fontSize: 11, fill: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7] }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={12}
            />
            <YAxis
              orientation="right"
              tick={{ fontSize: 11, fill: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7] }}
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
              width={44}
              tickMargin={8}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;
                const totalValue = payload.reduce((sum, p) => sum + (Number(p.value) || 0), 0);
                return (
                  <div style={tooltipShellStyle as any}>
                    <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                    {payload.map((entry) => {
                      const value = Number(entry.value) || 0;
                      const pct = totalValue > 0 ? Math.round((value / totalValue) * 100) : 0;
                      return (
                        <div key={entry.name as string} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 2,
                              backgroundColor: entry.color as string,
                              display: 'inline-block',
                            }}
                          />
                          <span style={{ fontWeight: 700 }}>{value}</span>
                          <span style={{ opacity: 0.8 }}>({pct}%)</span>
                          <span style={{ opacity: 0.8 }}>{entry.name}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              }}
            />
            <Bar
              dataKey="unmitigatedNoPoam"
              name="No POAM"
              stackId="poam"
              fill={pipelineColors[0]}
                  barSize={18}
              shape={(props: any) =>
                roundedTopShape(
                  props,
                  (props?.payload?.poamDevelopedNotMonitored ?? 0) === 0 &&
                    (props?.payload?.poamOpenNotDeveloped ?? 0) === 0 &&
                    (props?.payload?.unmitigatedNoPoam ?? 0) > 0,
                )
              }
              isAnimationActive={false}
            />
            <Bar
              dataKey="poamOpenNotDeveloped"
              name="Open, Not Developed"
              stackId="poam"
              fill={pipelineColors[1]}
                  barSize={18}
              shape={(props: any) =>
                roundedTopShape(
                  props,
                  (props?.payload?.poamDevelopedNotMonitored ?? 0) === 0 &&
                    (props?.payload?.poamOpenNotDeveloped ?? 0) > 0,
                )
              }
              isAnimationActive={false}
            />
            <Bar
              dataKey="poamDevelopedNotMonitored"
              name="Developed, Not Monitored"
              stackId="poam"
              fill={pipelineColors[2]}
                  barSize={18}
              shape={(props: any) => roundedTopShape(props, (props?.payload?.poamDevelopedNotMonitored ?? 0) > 0)}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px 12px',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: 12,
        }}
      >
        {buckets.map((bucket) => (
          <Box key={bucket.key} sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: bucket.color,
                flex: '0 0 auto',
              }}
            />
            <Text size="xs" color="dimmed">
              {bucket.label}
            </Text>
          </Box>
        ))}
      </Box>
    </SafeTCardShell>
  );
}
